import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../context/LanguageContext';
import { SERVICE_CARDS } from '../config/landings';
import { ArrowRight, Target } from 'lucide-react';

export const HomeServiceCards = () => {
  const { language } = useLanguage();
  const copy = SERVICE_CARDS[language] || SERVICE_CARDS.es;
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [hovered, setHovered] = useState(-1);

  return (
    <section
      id="clases-por-objetivo"
      className="py-20 md:py-24 bg-gradient-to-b from-[#E8F5FA] to-white"
      data-testid="home-service-cards"
    >
      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <span className="inline-flex items-center gap-2 text-[#E38987] font-semibold text-xs uppercase tracking-[0.18em]">
            <Target className="w-4 h-4" />
            {copy.kicker}
          </span>
          <h2
            className="text-3xl md:text-4xl font-bold text-[#4A4A4A] mt-4 mb-5 leading-tight"
            data-testid="service-cards-title"
          >
            {copy.title}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-[#A8D8EA] to-[#F5A6A4] mb-6" />
          <p className="text-base text-gray-600 leading-relaxed">{copy.lead}</p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {copy.cards.map((card, i) => (
            <motion.div
              key={card.path}
              initial={{ opacity: 0, y: 24 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.08 * i }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(-1)}
              className="h-full"
            >
              <Link
                to={card.path}
                data-testid={`service-card-${i}`}
                className="group flex h-full flex-col justify-between p-6 rounded-3xl bg-white border border-[#A8D8EA]/40 shadow-sm hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7DBDD9]"
              >
                <div>
                  <span className="block w-10 h-1 rounded-full bg-gradient-to-r from-[#A8D8EA] to-[#F5A6A4] mb-5" />
                  <h3 className="text-base font-bold text-[#4A4A4A] mb-3 leading-snug">{card.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{card.description}</p>
                </div>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#5DA6C9]">
                  {copy.more}
                  <ArrowRight className={`w-4 h-4 transition-transform ${hovered === i ? 'translate-x-1' : ''}`} />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
